import { useState, useMemo, useEffect, useRef } from 'react';
import '../../../assets/css/StudentDashboard.css';
import { getBlob, getData } from '../../../API/GlobalApi.js';
import SearchingBar from '../../../Utils/Searching.js';
import Notifier from '../../../Utils/notify.js';
import AdvanceTable from '../../../Utils/AdvanceTables.js';
import TableSkeleton from '../../../Utils/TableSkeleton.js';
import exporticon from '../../../assets/icons/export-data.svg';
import debit from '../../../assets/icons/rupee-line-circle-svgrepo-com.svg';
import { highlightMatch } from '../../../Utils/highlightSearchFunctions.js';
import { reactSelectStyles } from '../../../Utils/selectBoxStyle.js';
import Select from 'react-select';
import SpinnerLoader from '../../../Utils/Spinner.js';
import ProfileStatus from '../../../Component/ProfileStatus.js';
import TableLoaderOverlay from '../../../Utils/tableFadingLoader.js';
import { useTableFade } from '../../../Utils/tableFadeHooks.js';

const REPORT_URL = 'finance/reports/course-wise-ledger/';
const EXPORT_URL = 'finance/reports/course-wise-ledger/export/excel/';

const formatINR = (value) =>
    Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CourseWiseLedgerReport = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSearchLoading, setSearchLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [reportData, setReportData] = useState([]);
    const [filterQueryString, setFilterQueryString] = useState({});
    const [filterLoading, setFilterLoading] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [summary, setSummary] = useState({ total_credit: 0, total_debit: 0, total_balance: 0, student_count: 0 });

    const [courseOptions, setCourseOptions] = useState([]);
    const [semesterOptions, setSemesterOptions] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedSemester, setSelectedSemester] = useState(null);

    const [academicYearOptions, setAcademicYearOptions] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);

    const pageSize = 10;
    const pageCounts = Math.ceil(totalCount / pageSize);

    const { skipSpinnerRef, tableLoading, triggerTableLoading, stopTableLoading } = useTableFade();

    const getReport = async (page = 0, field = sortField, order = sortOrder, query = filterQueryString) => {
        const pageNumber = page + 1;
        const params = new URLSearchParams(query || {});
        params.set('page', pageNumber);
        if (field) params.set('type', field);
        if (order) params.set('sort_by', order);

        if (!skipSpinnerRef.current) {
            if (isInitialLoad) setIsLoading(true);
            else setSearchLoading(true);
        }
        try {
            const response = await getData(`${REPORT_URL}?${params.toString()}`);
            setReportData(response?.results || []);
            setTotalCount(response?.count || 0);
            setSummary(response?.summary || { total_credit: 0, total_debit: 0, total_balance: 0, student_count: 0 });
        } catch (error) {
            Notifier.error('Failed to load report');
            setReportData([]);
            setTotalCount(0);
        } finally {
            if (!skipSpinnerRef.current) {
                if (isInitialLoad) {
                    setIsLoading(false);
                    setIsInitialLoad(false);
                } else {
                    setSearchLoading(false);
                }
            }
            setFilterLoading(false);
            skipSpinnerRef.current = false;
        }
    };

    useEffect(() => {
        getReport(currentPage).finally(() => stopTableLoading());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filterQueryString]);

    const handlePageChange = ({ selected }) => {
        triggerTableLoading();
        setCurrentPage(selected);
    };

    const handleSort = (field, order) => {
        setSortField(field);
        setSortOrder(order);
        setCurrentPage(0);
        setFilterQueryString((prev) => ({ ...prev, type: field, sort_by: order }));
    };

    // ── Course / Semester dependent dropdowns ──
    const getCourses = async () => {
        try {
            const response = await getData('academics/get-all-courses-unpaginated/');
            if (response?.data?.length) {
                setCourseOptions(
                    response.data.map((item) => ({
                        label: item.name,
                        value: item.id,
                        academic_groups: item.academic_groups || [],
                    }))
                );
            }
        } catch (error) {
            setCourseOptions([]);
        }
    };

    const getAcademicYears = async () => {
        try {
            const response = await getData('academics/academic-years/');
            const years = Array.isArray(response) ? response : response?.results || [];
            setAcademicYearOptions(
                years
                    .filter((y) => y?.name)
                    .map((y) => ({ label: y.name, value: y.id }))
                    // newest year first (labels are "YYYY-YYYY")
                    .sort((a, b) => b.label.localeCompare(a.label))
            );
        } catch (error) {
            setAcademicYearOptions([]);
        }
    };

    useEffect(() => {
        getCourses();
        getAcademicYears();
    }, []);

    const handleAcademicYearChange = (year) => {
        setSelectedAcademicYear(year);
        setCurrentPage(0);
        skipSpinnerRef.current = true;
        setFilterLoading(true);
        setFilterQueryString((prev) => ({
            ...prev,
            academic_year_id: year?.value || '',
        }));
    };

    const handleCourseChange = (course) => {
        setSelectedCourse(course);
        setSelectedSemester(null);
        setSemesterOptions(
            (course?.academic_groups || []).map((ag) => ({
                label: ag.name,
                value: ag.academic_group_id,
            }))
        );
        setCurrentPage(0);
        skipSpinnerRef.current = true;
        setFilterLoading(true);
        setFilterQueryString((prev) => ({
            ...prev,
            specialization_id: course?.value || '',
            academic_group_id: '',
        }));
    };

    const handleSemesterChange = (sem) => {
        setSelectedSemester(sem);
        setCurrentPage(0);
        skipSpinnerRef.current = true;
        setFilterLoading(true);
        setFilterQueryString((prev) => ({
            ...prev,
            academic_group_id: sem?.value || '',
        }));
    };

    const handleExportExcel = async () => {
        try {
            const queryToSend = { ...filterQueryString };
            delete queryToSend.page;
            const params = new URLSearchParams(queryToSend);
            const blob = await getBlob(`${EXPORT_URL}?${params.toString()}`);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const courseName = selectedCourse?.label?.replace(/\s+/g, '_') || 'AllCourses';
            a.download = `course_wise_ledger_${courseName}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            Notifier.success('Excel exported successfully');
        } catch (error) {
            Notifier.error('❌ Excel export failed');
        }
    };

    const searchValue = new URLSearchParams(filterQueryString).get('search') || '';
    const searchInputRef = useRef(null);

    const formattedData = useMemo(() => {
        return (reportData || []).map((std) => ({
            id: std.student_id,
            name: std.student_name,
            registration_number: std.permanent_registration_number,
            course: [std.course, std.specialization].filter(Boolean).join(' '),
            academic_group: [std.academic_group, std.lag].filter(Boolean).join(' '),
            academic_year: std.academic_year,
            mobile: std.mobile,
            total_credit: std.total_credit,
            total_debit: std.total_debit,
            balance: std.balance,
        }));
    }, [reportData]);

    const columns = useMemo(
        () => [
            {
                Header: () => <p style={{ textAlign: 'left' }}>Student Name</p>,
                accessor: 'name',
                disableSortBy: false,
                Cell: ({ row }) => (
                    <p
                        className="no-text-cursor"
                        style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}
                        dangerouslySetInnerHTML={{
                            __html: highlightMatch(row.original.name?.toString() || '-', searchValue),
                        }}
                    />
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Registration No.</p>,
                accessor: 'registration_number',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <p
                        className="no-text-cursor"
                        style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}
                        dangerouslySetInnerHTML={{
                            __html: highlightMatch(row.original.registration_number?.toString() || '-', searchValue),
                        }}
                    />
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Course</p>,
                accessor: 'course',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>{row.original.course || '-'}</p>
                    </div>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Semester / Year</p>,
                accessor: 'academic_group',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>{row.original.academic_group || '-'}</p>
                    </div>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Academic Year</p>,
                accessor: 'academic_year',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>{row.original.academic_year || '-'}</p>
                    </div>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'left' }}>Contact</p>,
                accessor: 'mobile',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <p
                        className="no-text-cursor"
                        style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}
                        dangerouslySetInnerHTML={{
                            __html: highlightMatch(row.original.mobile?.toString() || '-', searchValue),
                        }}
                    />
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'right' }}>Credit (Paid)</p>,
                accessor: 'total_credit',
                disableSortBy: false,
                headerJustify: 'end',
                Cell: ({ row }) => (
                    <div style={{ textAlign: 'right', color: '#39886F', margin: 0 }} className="no-text-cursor">
                        ₹ {formatINR(row.original.total_credit)}
                    </div>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'right' }}>Debit (Charged)</p>,
                accessor: 'total_debit',
                disableSortBy: false,
                headerJustify: 'end',
                Cell: ({ row }) => (
                    <div style={{ textAlign: 'right', color: '#222F3E', margin: 0 }} className="no-text-cursor">
                        ₹ {formatINR(row.original.total_debit)}
                    </div>
                ),
            },
            {
                Header: () => <p style={{ textAlign: 'right' }}>Balance Due</p>,
                accessor: 'balance',
                disableSortBy: false,
                headerJustify: 'end',
                Cell: ({ row }) => {
                    const bal = row.original.balance ?? 0;
                    // > 0 => student owes (red); <= 0 => settled / advance (green)
                    const color = bal > 0 ? 'red' : 'green';
                    return (
                        <div style={{ textAlign: 'right', color, margin: 0 }} className="no-text-cursor">
                            ₹ {formatINR(bal)}
                        </div>
                    );
                },
            },
        ],
        [searchValue]
    );

    return (
        <>
            {isLoading ? (
                <TableSkeleton rows={10} columns={9} showHeader={true} />
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {/* ── Title + Summary cards ── */}
                    <div style={{ flexShrink: 0, padding: '0 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                            <h2 className="main-heading">Course-wise Ledger Report ({totalCount})</h2>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <ProfileStatus
                                    label="Total Credit (Paid)" icon={debit}
                                    iconColor="#39886F" bgColor="#E6F4EF" circleColor="#39886F"
                                    amountsData={formatINR(summary.total_credit)} height="60px" widths="230px"
                                />
                                <ProfileStatus
                                    label="Total Debit (Charged)" icon={debit}
                                    iconColor="#222F3E" bgColor="#EFEFF4" circleColor="#222F3E"
                                    amountsData={formatINR(summary.total_debit)} height="60px" widths="230px"
                                />
                                <ProfileStatus
                                    label="Total Balance Due" icon={debit}
                                    iconColor="red" bgColor="#FDE2E2" circleColor="#39886F"
                                    amountsData={formatINR(summary.total_balance)} height="60px" widths="230px"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Search + Filters ── */}
                    <div style={{ flexShrink: 0, padding: '8px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                            <SearchingBar
                                placeholder="Search by Student name, Registration No., Contact No. ...."
                                url={`/${REPORT_URL}`}
                                filterQueryString={filterQueryString}
                                setResults={setReportData}
                                ref={searchInputRef}
                                setTotalCount={setTotalCount}
                                setFilterQueryString={setFilterQueryString}
                                setSearchLoading={setSearchLoading}
                                setCurrentPage={setCurrentPage}
                            />
                            <div style={{ width: '170px' }}>
                                <Select
                                    options={academicYearOptions}
                                    value={selectedAcademicYear}
                                    onChange={handleAcademicYearChange}
                                    placeholder="Academic Year"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={{
                                        ...reactSelectStyles,
                                        control: (base, state) => ({
                                            ...reactSelectStyles.control?.(base, state),
                                            minHeight: '34px',
                                        }),
                                    }}
                                />
                            </div>
                            <div style={{ width: '220px' }}>
                                <Select
                                    options={courseOptions}
                                    value={selectedCourse}
                                    onChange={handleCourseChange}
                                    placeholder="Select Course"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={{
                                        ...reactSelectStyles,
                                        control: (base, state) => ({
                                            ...reactSelectStyles.control?.(base, state),
                                            minHeight: '34px',
                                        }),
                                    }}
                                />
                            </div>
                            <div style={{ width: '180px' }}>
                                <Select
                                    options={semesterOptions}
                                    value={selectedSemester}
                                    onChange={handleSemesterChange}
                                    placeholder="Semester / Year"
                                    isDisabled={!selectedCourse}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isClearable
                                    styles={{
                                        ...reactSelectStyles,
                                        control: (base, state) => ({
                                            ...reactSelectStyles.control?.(base, state),
                                            minHeight: '34px',
                                        }),
                                    }}
                                />
                            </div>
                            <button
                                className="filter-btn"
                                style={{ background: 'white', border: '1px solid black', height: '35px' }}
                                title="Export Data"
                                onClick={handleExportExcel}
                            >
                                <img src={exporticon} alt="Export" />
                            </button>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            padding: '0 12px',
                            position: 'relative',
                        }}
                    >
                        {filterLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                <SpinnerLoader />
                            </div>
                        ) : formattedData?.length > 0 ? (
                            <>
                                <AdvanceTable
                                    columns={columns}
                                    pageCounts={pageCounts}
                                    handlePageChange={handlePageChange}
                                    data={formattedData}
                                    currentPage={currentPage}
                                    checkbox={false}
                                    handleSort={handleSort}
                                    sortField={sortField}
                                    sortOrder={sortOrder}
                                />
                                <TableLoaderOverlay isLoading={tableLoading} />
                            </>
                        ) : (
                            !isLoading &&
                            !isSearchLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: 1,
                                        color: '#888',
                                        textAlign: 'center',
                                    }}
                                >
                                    No ledger records found.
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default CourseWiseLedgerReport;
